import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Icon } from '@phosphor-icons/react';
import {
    BellSimpleIcon,
    CalendarBlankIcon,
    ChartBarIcon,
    CubeIcon,
    SecurityCameraIcon,
} from '@phosphor-icons/react';
import {
    usePopoverCloseSignal,
    useQueryFiltersStore,
    useSourcesStore,
} from '@/stores';
import {
    useSortFromUrl,
    useFeatureFlags,
    useRetentionStartDate,
    useVideoSourceSelected,
} from '@/hooks';
import {
    DateTimeMode,
    SortOption,
    getDateRangeFromMode,
} from '@/utils/query-filters';
import { SourceType } from '@/types/source';
import { ConfidenceFilter, DateTimeRangePicker } from '@/components';
import {
    ActionBar,
    ActionType,
    buildFiltersAction,
    buildMultiSelectToggle,
    buildSortAction,
} from '@/components/ActionBar';
import type { ActionConfig, MenuItemRow } from '@/components/ActionBar';
import { SourcesMenu } from '@/features/SourcesMenu';
import { AlertRulesMenu } from '@/features/AlertRulesMenu';
import { ObjectTypeFilter } from '@/features/ObjectTypeFilter';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import {
    EventFilter,
    EventsBrowseBarConfig,
    EventSort,
} from './EventsBrowseBar.types';

interface EventsBrowseBarProps {
    config: EventsBrowseBarConfig;
    sourcesMenuMode?: 'all' | 'cameras';
    initialSortOption?: SortOption;
    sortOption?: SortOption;
    setSortOption?: (option: SortOption) => void;
    onEnterMultiSelect?: () => void;
}

export function EventsBrowseBar({
    config,
    sourcesMenuMode = 'all',
    initialSortOption,
    sortOption: controlledSortOption,
    setSortOption: controlledSetSortOption,
    onEnterMultiSelect,
}: EventsBrowseBarProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const { alertsEnabled } = useFeatureFlags();
    const retentionStart = useRetentionStartDate();
    const [urlSortOption, setUrlSortOption] = useSortFromUrl(
        initialSortOption ?? SortOption.CONFIDENCE,
    );
    const sortOption = controlledSortOption ?? urlSortOption;
    const setSortOption = controlledSetSortOption ?? setUrlSortOption;
    const {
        filters,
        setDateTimeFilter,
        clearDateTimeFilter,
        setConfidenceFilter,
        selectedObjectClassIds,
        setObjectClassSelection,
        clearObjectClassSelection,
        selectedAlertRuleIds,
    } = useQueryFiltersStore();
    const triggerPopoverClose = usePopoverCloseSignal((state) => state.trigger);
    const selected = useSourcesStore((state) => state.selected);
    const sourcesMap = useSourcesStore((state) => state.sourcesMap);
    const videoSourceSelected = useVideoSourceSelected(selected);
    const setSelectedSources = useSourcesStore(
        (state) => state.setSelectedSources,
    );

    useEffect(() => {
        if (sourcesMenuMode !== 'cameras' || selected.size === 0) {
            return;
        }

        const filteredSelection = new Set(
            Array.from(selected).filter((id) => {
                const source = sourcesMap.get(id);
                if (!source) return true;
                return source.type === SourceType.CAMERA;
            }),
        );

        if (filteredSelection.size !== selected.size) {
            setSelectedSources(filteredSelection);
        }
    }, [sourcesMenuMode, selected, sourcesMap, setSelectedSources]);

    const resolvedDateRange =
        filters.dateTime.mode === DateTimeMode.CUSTOM
            ? filters.dateTime.customRange
            : getDateRangeFromMode(filters.dateTime.mode);

    const filterItemMap: Record<EventFilter, MenuItemRow> = {
        [EventFilter.CONFIDENCE]: {
            icon: ChartBarIcon,
            label: t('browseBar.confidenceLevel'),
            isActive: filters.confidence.enabled,
            children: (
                <ConfidenceFilter
                    min={filters.confidence.min}
                    onApply={(range) => {
                        setConfidenceFilter(true, range.min, range.max);
                    }}
                    onClear={() => {
                        setConfidenceFilter(false);
                    }}
                    onClose={triggerPopoverClose}
                />
            ),
        },
        [EventFilter.DATE_RANGE]: {
            icon: CalendarBlankIcon,
            label: t('browseBar.dateAndTime'),
            isActive: filters.dateTime.mode !== DateTimeMode.ALL,
            children: (
                <DateTimeRangePicker
                    initialStartDate={resolvedDateRange?.startDate}
                    initialEndDate={resolvedDateRange?.endDate}
                    initialMode={filters.dateTime.mode}
                    earliestDate={retentionStart}
                    onApply={(range) => {
                        if (range.startTime && range.endTime) {
                            setDateTimeFilter(range.mode, {
                                startDate: range.startTime,
                                endDate: range.endTime,
                            });
                        }
                    }}
                    onClear={() => {
                        clearDateTimeFilter();
                    }}
                    onClose={triggerPopoverClose}
                />
            ),
        },
        [EventFilter.SOURCE]: {
            icon: SecurityCameraIconMirrored as Icon,
            label: t('browseBar.sources'),
            isActive: selected.size > 0,
            children: (
                <SourcesMenu
                    includeVideos={sourcesMenuMode !== 'cameras'}
                    onClose={triggerPopoverClose}
                />
            ),
        },
        [EventFilter.ALERT_RULE]: {
            icon: BellSimpleIcon,
            label: t('browseBar.alertRules'),
            isActive: selectedAlertRuleIds.size > 0,
            children: <AlertRulesMenu onClose={triggerPopoverClose} />,
        },
        [EventFilter.OBJECT_TYPE]: {
            icon: CubeIcon,
            label: t('browseBar.objectType'),
            isActive: selectedObjectClassIds.size > 0,
            children: (
                <ObjectTypeFilter
                    selectedClassIds={selectedObjectClassIds}
                    onChange={setObjectClassSelection}
                    onClear={clearObjectClassSelection}
                />
            ),
        },
    };

    const sortItemMap: Record<EventSort, MenuItemRow[]> = {
        [EventSort.CONFIDENCE]: [
            {
                icon: ChartBarIcon,
                label: t('browseBar.confidenceLevel'),
                isActive: sortOption === SortOption.CONFIDENCE,
                onClick: () => setSortOption(SortOption.CONFIDENCE),
            },
        ],
        [EventSort.DATE]: [
            {
                icon: CalendarBlankIcon,
                label: t('browseBar.eventDateNewest'),
                isActive: sortOption === SortOption.DATE_NEWEST,
                onClick: () => setSortOption(SortOption.DATE_NEWEST),
            },
            {
                icon: CalendarBlankIcon,
                label: t('browseBar.eventDateOldest'),
                isActive: sortOption === SortOption.DATE_OLDEST,
                onClick: () => setSortOption(SortOption.DATE_OLDEST),
            },
        ],
    };

    const allConfiguredFilters = config.filters ?? [];
    const nonSourceFilters = allConfiguredFilters
        .filter((f) => f !== EventFilter.SOURCE)
        .filter((f) => alertsEnabled || f !== EventFilter.ALERT_RULE)
        .filter((f) => !videoSourceSelected || f !== EventFilter.DATE_RANGE);
    const hasSourceFilter = allConfiguredFilters.includes(EventFilter.SOURCE);

    const hasActiveFilter = nonSourceFilters.some(
        (f) => filterItemMap[f].isActive,
    );

    const filtersAction: ActionConfig | undefined = nonSourceFilters.length
        ? buildFiltersAction(t, {
              isActive: hasActiveFilter,
              items: nonSourceFilters.map((f) => filterItemMap[f]),
          })
        : undefined;

    const sourceAction: ActionConfig | undefined = hasSourceFilter
        ? {
              type: ActionType.ITEM,
              icon: SecurityCameraIconMirrored as Icon,
              label: t('browseBar.sources'),
              active: selected.size > 0,
              onClear:
                  selected.size > 0 && !config.requireSource
                      ? () => {
                            setSelectedSources(new Set());
                        }
                      : undefined,
              content: (
                  <SourcesMenu
                      includeVideos={sourcesMenuMode !== 'cameras'}
                      onClose={triggerPopoverClose}
                      submitLabel={t('browseBar.apply')}
                      requireSelection={config.requireSource}
                  />
              ),
          }
        : undefined;

    const sortAction: ActionConfig | undefined = config.sorts?.length
        ? buildSortAction(t, [
              ...config.sorts
                  .flatMap((s) => sortItemMap[s])
                  .sort((a, b) => a.label.localeCompare(b.label)),
              {
                  icon: SecurityCameraIcon,
                  label: t('browseBar.sourceAZ'),
                  isActive: sortOption === SortOption.SOURCE_A_Z,
                  onClick: () => setSortOption(SortOption.SOURCE_A_Z),
              },
              {
                  icon: SecurityCameraIcon,
                  label: t('browseBar.sourceZA'),
                  isActive: sortOption === SortOption.SOURCE_Z_A,
                  onClick: () => setSortOption(SortOption.SOURCE_Z_A),
              },
          ])
        : undefined;

    const actions: ActionConfig[] = [
        ...(filtersAction ? [filtersAction] : []),
        ...(sourceAction ? [sourceAction] : []),
        ...(sortAction ? [sortAction] : []),
        ...(onEnterMultiSelect
            ? [buildMultiSelectToggle(t, onEnterMultiSelect)]
            : []),
    ];

    return <ActionBar actions={actions} />;
}
